import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GetTaskInfoResponseData } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { FETCH_TASK_INFO_QUERY_KEY } from './tasks-query-keys';

/**
 * The detail shown beside a task — the device, the app version, the battery
 * and the rest of what the phone reported alongside it.
 */
export function useFetchTaskInfo(taskId: string): {
    status: 'error' | 'success' | 'pending';
    taskInfo: GetTaskInfoResponseData | undefined;
    refetch: () => void;
} {
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const { data, status, refetch } = useQuery({
        queryKey: [FETCH_TASK_INFO_QUERY_KEY, taskId],
        queryFn: () => {
            return openApiRef.current.tasks.getInfo(taskId).then((res) => {
                return res.data.data;
            });
        },
        // Nothing to read before a task has been chosen.
        enabled: taskId !== ''
    });

    return {
        status,
        taskInfo: data,
        refetch: () => {
            void refetch();
        }
    };
}
